/* RMA Adress-Korrektur — Form auf /pages/rma-address?token=…
 *
 * Zwei Inquiry-Modi (per data.inquiryKind aus dem load-Endpoint):
 *   - ADDRESS       → Form mit Adressfeldern (DHL-Adressproblem)
 *   - HOLD_EXPIRED  → zwei Buttons "Erneut senden" / "Geld zurück"
 *                    (Kunde hat Paket nicht abgeholt, Adresse stimmt)
 *
 * State-Switching:
 *   LOADING → FORM | DECIDE | ALREADY_RESPONDED | EXPIRED | NOT_FOUND | ERROR
 *   FORM/DECIDE → REFUND_CHOICE (Geld vs. Gutschein) → SUCCESS_REFUND
 *   FORM/DECIDE → SUCCESS_ADDRESS | SUCCESS_RESHIP | ERROR
 *
 * Alle UI-Texte kommen aus den Section-Settings (data-strings am Container).
 */
(function () {
  'use strict';

  const root = document.querySelector('.rma-address-page');
  if (!root) return;

  const strings = JSON.parse(decodeHtmlEntities(root.dataset.strings || '{}'));
  const form = root.querySelector('[data-form="address"]');

  const params = new URLSearchParams(window.location.search);
  const token = (params.get('token') || '').trim();

  if (!token) {
    showState('NOT_FOUND');
    return;
  }

  loadInquiry().catch((err) => {
    console.error('[rma-address] load failed', err);
    showState('ERROR');
  });

  // ───────────────────────────────────────────────────────────────────────
  async function loadInquiry() {
    const res = await fetch(`/apps/rma/api/address-correction/load?token=${encodeURIComponent(token)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      showState('ERROR');
      return;
    }
    const data = await res.json();
    // Lokalisierte UI-Texte aus dem Backend (Sprache des Falls) über die
    // deutschen Section-Defaults legen und auf alle [data-i18n]-Elemente
    // anwenden — vor swapHeader, damit kind-spezifische Titel gewinnen.
    if (data && data.strings && typeof data.strings === 'object') {
      Object.assign(strings, data.strings);
      applyStrings();
    }
    if (!data || data.ok === false) {
      const status = data?.status || 'not-found';
      showState(statusToUiState(status));
      return;
    }
    if (data.ok && data.status === 'ok') {
      // INBOUND_RETURN (Rücksendung an Absender) nutzt denselben DECIDE-State
      // wie HOLD_EXPIRED: er bietet bereits alle vier Wege — andere Adresse
      // (→FORM), gleiche Adresse erneut (confirm-reship) und Erstattung
      // (→REFUND_CHOICE: Geld oder Gutschein). Nur Überschrift/Intro abweichend.
      const kind =
        data.inquiryKind === 'HOLD_EXPIRED' ? 'HOLD_EXPIRED'
        : data.inquiryKind === 'INBOUND_RETURN' ? 'INBOUND_RETURN'
        : 'ADDRESS';
      if (kind === 'HOLD_EXPIRED' || kind === 'INBOUND_RETURN') {
        if (kind === 'INBOUND_RETURN') {
          swapHeader(strings.page_title_inboundreturn, strings.page_subtitle_inboundreturn);
        } else {
          swapHeader(strings.page_title_holdexpired, strings.page_subtitle_holdexpired);
        }
        renderCurrentAddress(data.currentAddress);
        // Form schon mal vorbefüllen — falls Kunde "An andere Adresse senden"
        // klickt, wechselt das JS auf den FORM-State und die Felder sind ready.
        prefillForm(data.currentAddress);
        attachDecideActions();
        showState('DECIDE');
      } else {
        prefillForm(data.currentAddress);
        renderHint(data.addressHint);
        attachFormActions();
        showState('FORM');
      }
      return;
    }
    showState('ERROR');
  }

  function swapHeader(title, subtitle) {
    const h1 = root.querySelector('[data-target="page-title"]');
    const p = root.querySelector('[data-target="page-subtitle"]');
    if (h1 && typeof title === 'string' && title.trim().length > 0) {
      h1.textContent = title;
    }
    if (p) {
      if (typeof subtitle === 'string' && subtitle.trim().length > 0) {
        p.textContent = subtitle;
      } else {
        p.hidden = true;
      }
    }
  }

  function renderHint(text) {
    const el = root.querySelector('[data-target="address-hint"]');
    if (!el) return;
    if (typeof text === 'string' && text.trim().length > 0) {
      el.textContent = text;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  function renderCurrentAddress(addr) {
    const target = root.querySelector('[data-target="current-address-block"]');
    if (!target || !addr) return;
    const lines = [
      [addr.firstName, addr.lastName].filter(Boolean).join(' '),
      addr.company,
      addr.street,
      addr.address2,
      `${addr.zip || ''} ${addr.city || ''}`.trim(),
      (addr.country || '').toUpperCase(),
    ].filter((l) => l && l.trim().length > 0);
    target.innerHTML = '';
    const pre = document.createElement('pre');
    pre.className = 'rma-address-success-block';
    pre.textContent = lines.join('\n');
    target.appendChild(pre);
  }

  function prefillForm(addr) {
    if (!addr) return;
    Object.entries(addr).forEach(([k, v]) => {
      const input = form?.querySelector(`[name="${k}"]`);
      if (input && typeof v === 'string') input.value = v;
    });
  }

  function attachFormActions() {
    form?.querySelector('[data-action="submit-address"]')?.addEventListener('click', submitAddress);
    form?.querySelector('[data-action="request-refund"]')?.addEventListener('click', showRefundChoice);
  }

  function attachDecideActions() {
    root.querySelector('[data-state="DECIDE"] [data-action="confirm-reship"]')
      ?.addEventListener('click', submitReship);
    root.querySelector('[data-state="DECIDE"] [data-action="request-refund"]')
      ?.addEventListener('click', showRefundChoice);
    root.querySelector('[data-state="DECIDE"] [data-action="change-address"]')
      ?.addEventListener('click', switchToAddressForm);
  }

  // Lagerfrist-Variante: Kunde will an eine andere Adresse senden lassen.
  // Wir wechseln vom DECIDE-State zum FORM-State (gleiche Form wie beim
  // Adress-Korrektur-Flow) und tauschen den Intro-Text, weil der Default
  // "Bitte prüfe und korrigiere die Adressfelder…" hier nicht passt.
  function switchToAddressForm() {
    const introEl = root.querySelector('[data-target="form-intro"]');
    if (introEl && typeof strings.form_intro_holdexpired === 'string' && strings.form_intro_holdexpired.trim().length > 0) {
      introEl.textContent = strings.form_intro_holdexpired;
    }
    renderHint(null); // kein DHL-Adresshinweis im Lagerfrist-Pfad
    attachFormActions();
    showState('FORM');
  }

  async function submitAddress() {
    const errEl = form?.querySelector('[data-error="form"]');
    if (errEl) errEl.textContent = '';
    const fd = new FormData(form);
    const street = (fd.get('street') || '').toString().trim();
    const zip = (fd.get('zip') || '').toString().trim();
    const city = (fd.get('city') || '').toString().trim();
    const country = (fd.get('country') || '').toString().trim();
    if (!street || !zip || !city || !country || !/^[A-Za-z]{2}$/.test(country)) {
      if (errEl) errEl.textContent = strings.err_form_invalid || 'Please fill out all required fields.';
      return;
    }

    const payload = {
      firstName: (fd.get('firstName') || '').toString().trim(),
      lastName: (fd.get('lastName') || '').toString().trim(),
      company: (fd.get('company') || '').toString().trim(),
      street,
      address2: (fd.get('address2') || '').toString().trim(),
      zip,
      city,
      country: country.toUpperCase(),
    };

    setSubmitting(true);
    try {
      const res = await fetch('/apps/rma/api/address-correction/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token, mode: 'apply-address', address: payload }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok === false) {
        const err = data?.error || 'error';
        if (err === 'expired') showState('EXPIRED');
        else if (err === 'already_responded') showState('ALREADY_RESPONDED');
        else if (err === 'closed') showState('CLOSED');
        else if (err === 'invalid_token') showState('NOT_FOUND');
        else if (err === 'invalid_address' && errEl) {
          errEl.textContent = strings.err_form_invalid || 'Address is incomplete.';
        } else if (err === 'no_change') {
          // Kundin hat die (vorbefüllte) Adresse nicht geändert. Statt eines
          // generischen Fehlers fragen wir einmal zurück: wirklich an genau
          // diese Adresse erneut senden? Bei Bestätigung läuft der Reship über
          // den confirm-reship-Pfad (gleiche Adresse, kein Adress-Update).
          showConfirmSame(payload);
        } else {
          showState('ERROR');
        }
        return;
      }
      renderAddressSuccess(payload);
      showState('SUCCESS_ADDRESS');
    } catch (err) {
      console.error('[rma-address] submit failed', err);
      showState('ERROR');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReship() {
    setSubmitting(true);
    try {
      const res = await fetch('/apps/rma/api/address-correction/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token, mode: 'confirm-reship' }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok === false) {
        const err = data?.error || 'error';
        if (err === 'expired') showState('EXPIRED');
        else if (err === 'already_responded') showState('ALREADY_RESPONDED');
        else if (err === 'closed') showState('CLOSED');
        else if (err === 'invalid_token') showState('NOT_FOUND');
        else showState('ERROR');
        return;
      }
      showState('SUCCESS_RESHIP');
    } catch (err) {
      console.error('[rma-address] reship submit failed', err);
      showState('ERROR');
    } finally {
      setSubmitting(false);
    }
  }

  // Kunde will eine Erstattung — zuerst Geld-vs-Gutschein-Wahl anzeigen,
  // statt direkt zu submitten. Die eigentliche Entscheidung läuft über
  // submitRefundKind('money'|'voucher').
  let refundChoiceAttached = false;
  function showRefundChoice() {
    if (!refundChoiceAttached) {
      root.querySelector('[data-state="REFUND_CHOICE"] [data-action="refund-money"]')
        ?.addEventListener('click', () => submitRefundKind('money'));
      root.querySelector('[data-state="REFUND_CHOICE"] [data-action="refund-voucher"]')
        ?.addEventListener('click', () => submitRefundKind('voucher'));
      refundChoiceAttached = true;
    }
    showState('REFUND_CHOICE');
  }

  async function submitRefundKind(kind) {
    const mode = kind === 'money' ? 'request-money' : 'request-voucher';
    setSubmitting(true);
    try {
      const res = await fetch('/apps/rma/api/address-correction/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token, mode }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok === false) {
        const err = data?.error || 'error';
        if (err === 'expired') showState('EXPIRED');
        else if (err === 'already_responded') showState('ALREADY_RESPONDED');
        else if (err === 'closed') showState('CLOSED');
        else if (err === 'invalid_token') showState('NOT_FOUND');
        else showState('ERROR');
        return;
      }
      applyRefundSuccessText(kind);
      showState('SUCCESS_REFUND');
    } catch (err) {
      console.error('[rma-address] refund submit failed', err);
      showState('ERROR');
    } finally {
      setSubmitting(false);
    }
  }

  // SUCCESS_REFUND-Texte je nach Geld/Gutschein-Wahl setzen.
  function applyRefundSuccessText(kind) {
    const headingEl = root.querySelector('[data-state="SUCCESS_REFUND"] [data-target="refund-success-heading"]');
    const introEl = root.querySelector('[data-state="SUCCESS_REFUND"] [data-target="refund-success-intro"]');
    const heading = kind === 'money' ? strings.success_refund_money_heading : strings.success_refund_voucher_heading;
    const intro = kind === 'money' ? strings.success_refund_money_intro : strings.success_refund_voucher_intro;
    if (headingEl && typeof heading === 'string' && heading.trim().length > 0) headingEl.textContent = heading;
    if (introEl && typeof intro === 'string' && intro.trim().length > 0) introEl.textContent = intro;
  }

  function renderAddressSuccess(addr) {
    const target = root.querySelector('[data-target="success-address-block"]');
    if (!target) return;
    const lines = [
      [addr.firstName, addr.lastName].filter(Boolean).join(' '),
      addr.company,
      addr.street,
      addr.address2,
      `${addr.zip} ${addr.city}`,
      (addr.country || '').toUpperCase(),
    ].filter(Boolean);
    target.innerHTML = '';
    const pre = document.createElement('pre');
    pre.className = 'rma-address-success-block';
    pre.textContent = lines.join('\n');
    target.appendChild(pre);
  }

  // Rückfrage-Seite, wenn die Kundin die Adresse unverändert abgeschickt hat.
  // Zeigt die Adresse nochmal an und lässt zwei Wege zu:
  //   - "Ja, genau diese Adresse" → submitReship (confirm-reship, gleiche Adresse)
  //   - "Adresse doch ändern"     → zurück zur Form
  let confirmSameAttached = false;
  function showConfirmSame(addr) {
    renderConfirmSameAddress(addr);
    if (!confirmSameAttached) {
      root.querySelector('[data-state="CONFIRM_SAME"] [data-action="confirm-same-reship"]')
        ?.addEventListener('click', submitReship);
      root.querySelector('[data-state="CONFIRM_SAME"] [data-action="confirm-same-edit"]')
        ?.addEventListener('click', () => showState('FORM'));
      confirmSameAttached = true;
    }
    showState('CONFIRM_SAME');
  }

  function renderConfirmSameAddress(addr) {
    const target = root.querySelector('[data-target="confirm-same-block"]');
    if (!target || !addr) return;
    const lines = [
      [addr.firstName, addr.lastName].filter(Boolean).join(' '),
      addr.company,
      addr.street,
      addr.address2,
      `${addr.zip || ''} ${addr.city || ''}`.trim(),
      (addr.country || '').toUpperCase(),
    ].filter((l) => l && l.trim().length > 0);
    target.innerHTML = '';
    const pre = document.createElement('pre');
    pre.className = 'rma-address-success-block';
    pre.textContent = lines.join('\n');
    target.appendChild(pre);
  }

  function setSubmitting(on) {
    root.querySelectorAll('button').forEach((b) => {
      b.disabled = !!on;
    });
  }

  // Wendet die (ggf. lokalisierten) strings auf alle [data-i18n]-Elemente an.
  // textContent ersetzt den deutschen Section-Default; bei Labels trägt das
  // gekapselte <span data-i18n> den Text, das <input> bleibt unberührt.
  function applyStrings() {
    root.querySelectorAll('[data-i18n]').forEach((el) => {
      const v = strings[el.getAttribute('data-i18n')];
      if (typeof v === 'string' && v.length > 0) el.textContent = v;
    });
  }

  function showState(name) {
    root.querySelectorAll('[data-state]').forEach((el) => {
      el.hidden = el.getAttribute('data-state') !== name;
    });
  }

  function statusToUiState(status) {
    if (status === 'already-responded') return 'ALREADY_RESPONDED';
    if (status === 'expired') return 'EXPIRED';
    if (status === 'closed') return 'CLOSED';
    if (status === 'not-found') return 'NOT_FOUND';
    if (status === 'no-order') return 'ERROR';
    return 'ERROR';
  }

  function decodeHtmlEntities(s) {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }
})();
